/**
 * Tenants CopilotKit Actions
 * AI-executable functions for tenant management
 */

import { useCopilotAction, useDispatch } from '@ufc/core'
import { tenantsActions } from '../store/tenants.slice'
import type { TenantStatus, TenantChannel } from '../domain/types/tenant.types'

export function useTenantsCopilotActions() {
  const dispatch = useDispatch()

  useCopilotAction({
    name: 'searchTenants',
    description: 'Search for tenants by name, code, or legal name',
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'Search query to find tenants',
        required: true,
      },
    ],
    handler: async ({ query }) => {
      dispatch(tenantsActions.setFilter({ search: query }))
      return `Searching tenants for "${query}"`
    },
  })

  useCopilotAction({
    name: 'filterTenantsByStatus',
    description: 'Filter tenants by their status (pending, active, inactive, suspended)',
    parameters: [
      {
        name: 'status',
        type: 'string',
        description: 'Status to filter by: pending, active, inactive, suspended, or all',
        required: true,
      },
    ],
    handler: async ({ status }) => {
      dispatch(tenantsActions.setFilter({ status: status as TenantStatus | 'all' }))
      return `Filtering tenants by status: ${status}`
    },
  })

  useCopilotAction({
    name: 'filterTenantsByChannel',
    description: 'Filter tenants by their channel (b2b or ecommerce)',
    parameters: [
      {
        name: 'channel',
        type: 'string',
        description: 'Channel to filter by: b2b, ecommerce, or all',
        required: true,
      },
    ],
    handler: async ({ channel }) => {
      dispatch(tenantsActions.setFilter({ channel: channel as TenantChannel | 'all' }))
      return `Filtering tenants by channel: ${channel}`
    },
  })

  useCopilotAction({
    name: 'approveTenant',
    description: 'Approve a pending tenant and optionally unblock their users',
    parameters: [
      {
        name: 'tenantId',
        type: 'string',
        description: 'The ID of the tenant to approve',
        required: true,
      },
      {
        name: 'unblockUsers',
        type: 'boolean',
        description: 'Whether to unblock the tenant users',
        required: false,
      },
      {
        name: 'sapCustomerGroup',
        type: 'string',
        description: 'SAP KDGRP value (11=Transport, 12=Plants, 13=Bag Customers)',
        required: false,
      },
    ],
    handler: async ({ tenantId, unblockUsers, sapCustomerGroup }) => {
      dispatch(tenantsActions.approveTenant({ tenantId, unblockUsers, sapCustomerGroup }))
      return `Approving tenant ${tenantId}${unblockUsers ? ' and unblocking users' : ''}`
    },
  })

  useCopilotAction({
    name: 'suspendTenant',
    description: 'Suspend an active tenant',
    parameters: [
      {
        name: 'tenantId',
        type: 'string',
        description: 'The ID of the tenant to suspend',
        required: true,
      },
    ],
    handler: async ({ tenantId }) => {
      dispatch(tenantsActions.suspendTenant({ tenantId }))
      return `Suspending tenant ${tenantId}`
    },
  })

  useCopilotAction({
    name: 'clearTenantFilters',
    description: 'Clear all tenant filters and show all tenants',
    parameters: [],
    handler: async () => {
      dispatch(tenantsActions.clearFilters())
      return 'Cleared all tenant filters'
    },
  })

  useCopilotAction({
    name: 'refreshTenants',
    description: 'Refresh the tenants list from the server',
    parameters: [],
    handler: async () => {
      dispatch(tenantsActions.fetchTenants())
      return 'Refreshing tenants list'
    },
  })
}
